import React from 'react';
import PropTypes from 'prop-types';
import { useQuery, gql } from '@apollo/client';
import { IntlProvider, useIntl } from 'react-intl';
import OpportunityDetail from '../../components/StudentPlatform/pages/OpportunityDetail';
import ApolloWrapper from './ApolloWrapper';
import getMessages from '../../lib/i18n/getMessages';
import { CurrencyProvider } from '../../hooks/useCurrency';

const GET_HIRING_POST_DETAIL = gql`
  query GetHiringPostDetail($id: ID!) {
    currentUser {
      id
      name
      email
    }
    hiringPost(id: $id) {
      id
      title
      position_type
      location
      firm_name
      remote
      description
      requirements
      qualifications
      duration
      start_date
      compensation
      linkedin_url
      application_instructions
      status
      views_count
      applications_count
      published_at
      user {
        id
        name
        allowed_name
        profile_picture
        headline
        years_practicing
        designations
        advisor_licenses
      }
    }
  }
`;

const HiringPostDetailContainerInner = ({ postId }) => {
  const intl = useIntl();
  const { loading, error, data } = useQuery(GET_HIRING_POST_DETAIL, {
    variables: { id: postId },
    skip: !postId,
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-neutral-600">{intl.formatMessage({ id: 'hiring.container.detail.loading' })}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">{intl.formatMessage({ id: 'hiring.container.detail.error_heading' })}</h2>
          <p className="text-neutral-600 mb-4">{error.message}</p>
          <a
            href="/hiring"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {intl.formatMessage({ id: 'hiring.container.detail.back_link' })}
          </a>
        </div>
      </div>
    );
  }

  if (!data || !data.hiringPost) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-neutral-400 text-5xl mb-4">📭</div>
          <h2 className="text-xl font-semibold text-neutral-900 mb-2">{intl.formatMessage({ id: 'hiring.container.detail.not_found_heading' })}</h2>
          <p className="text-neutral-600 mb-4">{intl.formatMessage({ id: 'hiring.container.detail.not_found_body' })}</p>
          <a
            href="/hiring"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            {intl.formatMessage({ id: 'hiring.container.detail.back_link' })}
          </a>
        </div>
      </div>
    );
  }

  // Check if current user is the owner of the post
  const isOwnPost = data.currentUser && data.hiringPost.user &&
                     String(data.currentUser.id) === String(data.hiringPost.user.id);

  return (
    <OpportunityDetail
      currentUser={data.currentUser}
      opportunity={data.hiringPost}
      onSendMessage={null} // Advisors viewing their own posts don't need this
      isOwnPost={isOwnPost}
      backLinkUrl="/hiring"
      backLinkText="Back to Hiring Dashboard"
      showSaveShare={false}
      showNavbar={false}
    />
  );
};

HiringPostDetailContainerInner.propTypes = {
  postId: PropTypes.string,
};

const HiringPostDetailContainer = ({ postId }, railsContext) => {
  window.locale = (railsContext && railsContext.i18nLocale) || window.locale || 'en';
  window.currency = (railsContext && railsContext.currency) || window.currency || 'USD';
  const locale = window.locale;
  return (
    <CurrencyProvider currency={(railsContext && railsContext.currency) || window.currency || 'USD'}>
      <IntlProvider locale={locale} messages={getMessages(locale)}>
        <ApolloWrapper>
          <HiringPostDetailContainerInner postId={postId} />
        </ApolloWrapper>
      </IntlProvider>
    </CurrencyProvider>
  );
};

HiringPostDetailContainer.propTypes = {
  postId: PropTypes.string,
};

export default HiringPostDetailContainer;
